import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';

const STATUS_COLORS = {
  pending:   '#B07090',
  confirmed: '#5D9A57',
  completed: '#4A7FA5',
  cancelled: '#C0605A',
};

const STATUS_OPTIONS = ['pending', 'confirmed', 'completed', 'cancelled'];

export default function AdminAppointments() {
  const [appts, setAppts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('');
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [updating, setUpdating] = useState(null);
  const PER_PAGE = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: PER_PAGE });
    if (filter) params.set('status', filter);
    API.get(`/appointments?${params}`)
      .then(r => { if (r.data.success) { setAppts(r.data.data); setTotal(r.data.total); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const r = await API.patch(`/appointments/${id}/status`, { status });
      if (r.data.success) setAppts(prev => prev.map(a => a._id === id ? r.data.data : a));
    } finally { setUpdating(null); }
  };

  const deleteAppt = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    await API.delete(`/appointments/${id}`);
    setAppts(prev => prev.filter(a => a._id !== id));
    setTotal(t => t - 1);
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  const fmtTs   = (d) => new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h2 className="admin-page__title">Appointments</h2>
          <p className="admin-page__sub">{total} total · showing page {page}</p>
        </div>
        <div className="admin-page__actions">
          <select className="admin-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button className="admin-btn admin-btn--outline" onClick={load}>↺ Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">Loading appointments…</div>
      ) : appts.length === 0 ? (
        <div className="admin-empty">No appointments found.</div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Customer</th><th>Service</th><th>Date & Time</th>
                  <th>Status</th><th>Booked At</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {appts.map(a => (
                  <tr key={a._id}>
                    <td>
                      <div className="admin-table__name">{a.name}</div>
                      <div className="admin-table__sub">{a.phone}</div>
                      {a.email && <div className="admin-table__sub">{a.email}</div>}
                      {a.message && <div className="admin-table__note">"{a.message}"</div>}
                    </td>
                    <td>{a.service}</td>
                    <td>
                      <div>{fmtDate(a.date)}</div>
                      <div className="admin-table__sub">{a.time}</div>
                    </td>
                    <td>
                      <select
                        className="admin-status-select"
                        value={a.status}
                        onChange={e => updateStatus(a._id, e.target.value)}
                        disabled={updating === a._id}
                        style={{ color: STATUS_COLORS[a.status] }}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </td>
                    <td><div className="admin-table__sub">{fmtTs(a.createdAt)}</div></td>
                    <td>
                      <button className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => deleteAppt(a._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="admin-pagination">
            <button className="admin-btn admin-btn--outline admin-btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="admin-pagination__info">Page {page} of {Math.ceil(total / PER_PAGE)}</span>
            <button className="admin-btn admin-btn--outline admin-btn--sm" disabled={page * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}
